import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";

import { sessionMark } from "stores/sessionGuard";
/** Minimal shape of the `find` response rows + total we read off the store. */
interface FindResult<T> {
  data?: T[];
  total?: number;
}

export type AuditAction = "create" | "update" | "delete";

export interface AuditLogEntry {
  id: number;
  clientId: string | null;
  entityType: string;
  entityId: string;
  action: AuditAction;
  userId: number | null;
  beforeData: string | null;
  afterData: string | null;
  changedFields: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface UserSummary {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Every audited entityType — i.e. the exact string each API service passes to
 * `auditLog(...)` from the shared `src/hooks/audit-log.ts` hook. The filter
 * dropdown is seeded from this list and augmented with any other entityType
 * seen in loaded rows.
 *
 * These are entityType LABELS, not service paths, and the two differ: the
 * billing services log the singular `invoice` / `recurring-invoice` /
 * `client-billing-profile`, and users log `user`. Derived by grepping the API
 * for `auditLog(` — keep it in step with that grep, since a missing entry
 * silently hides a whole service from the filter until a row of that type
 * happens to be on the loaded page.
 */
export const AUDITED_SERVICES = [
  "api-keys",
  "client-billing-profile",
  "client-contacts",
  "client-integrations",
  "client-modules",
  "client-tools",
  "clients",
  "communications",
  "departments",
  "devops-connections",
  "devops-links",
  "devops-subscriptions",
  "documents",
  "invoice",
  "module-catalog",
  "portainer-connections",
  "recurring-invoice",
  "registry-connections",
  "roles",
  "user",
  "user-permissions",
  "user-roles",
] as const;

export type AuditActionFilter = "all" | AuditAction;

/**
 * The one key an audit row's `beforeData` carries when there IS no before-state
 * to carry. Mirrors `BEFORE_SNAPSHOT_UNAVAILABLE` in the API's
 * `src/hooks/audit-log.ts`, which writes it; its value is why the prior state
 * could not be read.
 *
 * It is NOT a column of the audited record, and everything on this screen that
 * enumerates fields has to know that. Without it, a `beforeData` holding only
 * the marker leaves `changedFields` null, the diff falls back to the union of
 * the two snapshots' keys, and the panel draws every field of the after-state
 * as having changed FROM NOTHING — an edit to a row we could not read renders
 * as a creation, with one extra row named after an internal key whose "before"
 * cell is the failure text. That fabrication is the reason the marker exists;
 * consuming it is what makes the screen honest.
 *
 * Pinned by literal in AuditLogStore.test.ts the way ENFORCED_SCOPES is: the
 * two halves cannot import from each other, so the string is spelled twice and
 * changing one without the other goes red.
 */
export const BEFORE_SNAPSHOT_UNAVAILABLE = "__beforeStateUnavailable";

/**
 * View store for the /admin/audit-log viewer.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The audit-log service is read through the registered
 * `audit-log` ServiceStore (see serviceStores/admin.stores.ts). Filtering and
 * pagination are SERVER-side (Feathers query syntax) since the table grows
 * without bound — so `entries`/`total` come from the `find` RESPONSE (the
 * current page + server count), NOT the accumulating entity cache, which under
 * server paging would union every page ever fetched.
 */
class AuditLogStore {
  rootStore: RootStore;

  entries: AuditLogEntry[] = [];
  total = 0;
  loading = false;
  error: string | null = null;

  // Server-side filters
  serviceFilter = "all";
  actionFilter: AuditActionFilter = "all";
  userFilter = ""; // numeric userId as text
  recordFilter = ""; // exact entityId
  clientFilter = ""; // exact clientId (uuid)
  dateFrom = ""; // YYYY-MM-DD
  dateTo = ""; // YYYY-MM-DD

  // Pagination
  page = 0;
  pageSize = 50;

  // Expanded diff rows
  expandedIds: Set<number> = new Set();

  // userId -> display name lookup (best-effort; failures leave raw ids)
  userNames: Map<number, string> = new Map();
  private usersLoaded = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `audit-log` ServiceStore (see serviceStores/admin.stores.ts). */
  private get auditLogService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("audit-log") ?? this.rootStore.auditLogStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * The builtin `user` ServiceStore (mounted by RootStore). Used only for the
   * best-effort id -> display-name directory.
   */
  private get userService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }

  // ---- Derived data -------------------------------------------------------

  get serviceOptions(): string[] {
    const seen = new Set<string>(AUDITED_SERVICES);
    for (const entry of this.entries) seen.add(entry.entityType);
    return Array.from(seen).sort();
  }

  userName(userId: number | null): string {
    if (userId === null || userId === undefined) return "system";
    return this.userNames.get(userId) || `user #${userId}`;
  }

  isExpanded(id: number): boolean {
    return this.expandedIds.has(id);
  }

  /** Parsed changed-field names for a row ([] when unavailable). */
  changedFieldList(entry: AuditLogEntry): string[] {
    if (!entry.changedFields) return [];
    try {
      const parsed = JSON.parse(entry.changedFields);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  /** Parse a before/after JSON snapshot into an object ({} when unavailable). */
  parseSnapshot(json: string | null): Record<string, unknown> {
    if (!json) return {};
    try {
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  /**
   * Why this entry's prior state could not be recovered, or null when it was.
   *
   * Presence of the marker key IS the fact; the value is the API's
   * classification of the failure (`not-found: …`, `driver:ETIMEOUT`). A marker
   * whose value is missing or not a string still means unavailable — the fact
   * must never be lost to a malformed reason — so it reports the absence rather
   * than falling back to "the state was fine".
   */
  beforeStateUnavailableReason(entry: AuditLogEntry): string | null {
    const before = this.parseSnapshot(entry.beforeData);
    if (!(BEFORE_SNAPSHOT_UNAVAILABLE in before)) return null;
    const reason = before[BEFORE_SNAPSHOT_UNAVAILABLE];
    return typeof reason === "string" && reason.trim() !== ""
      ? reason
      : "reason not recorded";
  }

  /**
   * Field-level diff rows for the expandable panel: one row per changed field
   * (updates), or every populated field (creates/deletes).
   *
   * The unavailable-before marker is never one of them — it is a statement
   * ABOUT the snapshot, not a column of the record. Callers must pair these
   * rows with `beforeStateUnavailableReason`: when that is non-null the "before"
   * side of every row is unknown rather than empty, and rendering them as a
   * before→after diff is the fabrication this screen exists not to commit.
   */
  diffRows(entry: AuditLogEntry): { field: string; before: string; after: string }[] {
    const before = this.parseSnapshot(entry.beforeData);
    const after = this.parseSnapshot(entry.afterData);
    const changed = this.changedFieldList(entry);
    const fields = (
      entry.action === "update" && changed.length > 0
        ? changed
        : Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    ).filter((field) => field !== BEFORE_SNAPSHOT_UNAVAILABLE);

    const display = (value: unknown): string => {
      if (value === undefined || value === null) return "—";
      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    };

    return fields.map((field) => ({
      field,
      before: display(before[field]),
      after: display(after[field]),
    }));
  }

  /**
   * The loaded page flattened to plain rows for CSV export. Deliberately scoped
   * to `entries` — the page currently on screen under the current filters — not
   * to the whole server-side table, which is unbounded and would need its own
   * paged fetch. The changed-field list is flattened to a single
   * semicolon-separated cell; for creates/deletes (which carry no
   * `changedFields`) it falls back to the field names in the snapshot diff, so
   * the column is never empty for a row that plainly changed something.
   *
   * That fallback is exactly what must NOT happen when the before-state is
   * unavailable. Those rows did change some subset of their fields — we simply
   * cannot say which — so listing every field of the after-state under a column
   * headed "Changed Fields" would state, in a file that outlives the screen,
   * something the ledger does not know. The cell is left empty and the reason
   * rides in its own `beforeStateUnavailable` column, which is empty for every
   * other row.
   */
  get exportRows() {
    return this.entries.map((entry) => {
      const unavailable = this.beforeStateUnavailableReason(entry);
      return {
        createdAt: entry.createdAt,
        entityType: entry.entityType,
        action: entry.action,
        entityId: entry.entityId,
        clientId: entry.clientId ?? "",
        user: this.userName(entry.userId),
        changedFields: unavailable
          ? ""
          : this.diffRows(entry)
              .map((row) => row.field)
              .join("; "),
        beforeStateUnavailable: unavailable ?? "",
        ipAddress: entry.ipAddress ?? "",
      };
    });
  }

  /** Page count implied by the server `total` and the active page size. */
  get pageCount(): number {
    if (this.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  /**
   * What `exportRows` actually covers, as a 1-based inclusive row range within
   * the server-side total. The export is page-scoped, and a CSV named
   * "audit-log" that silently holds 50 of 12,000 rows is exactly the kind of
   * number that lies — so the scope is carried in the file NAME and in the
   * button tooltip rather than left implicit. `from`/`to` are 0 when the page
   * is empty.
   */
  get exportScope(): {
    from: number;
    to: number;
    total: number;
    page: number;
    pageCount: number;
    /** True when the server holds rows this file does NOT contain. */
    partial: boolean;
  } {
    const count = this.entries.length;
    const from = count === 0 ? 0 : this.page * this.pageSize + 1;
    const to = count === 0 ? 0 : from + count - 1;
    return {
      from,
      to,
      total: this.total,
      page: this.page + 1,
      pageCount: this.pageCount,
      partial: count < this.total,
    };
  }

  /**
   * Filename for the CSV, scoped so the file is self-describing once it has
   * left the app: a partial export is stamped with the page it came from, a
   * complete one is not.
   */
  get exportFileName(): string {
    const stamp = new Date().toISOString().slice(0, 10);
    const { page, pageCount, partial } = this.exportScope;
    return partial
      ? `audit-log_page-${page}-of-${pageCount}_${stamp}`
      : `audit-log_${stamp}`;
  }

  // ---- Filter / pagination actions ---------------------------------------

  setServiceFilter(value: string) {
    this.serviceFilter = value;
    this.page = 0;
    void this.loadEntries();
  }

  setActionFilter(value: AuditActionFilter) {
    this.actionFilter = value;
    this.page = 0;
    void this.loadEntries();
  }

  setUserFilter(value: string) {
    this.userFilter = value.replace(/[^0-9]/g, "");
    this.page = 0;
  }

  setRecordFilter(value: string) {
    this.recordFilter = value;
    this.page = 0;
  }

  setClientFilter(value: string) {
    this.clientFilter = value;
    this.page = 0;
  }

  setDateFrom(value: string) {
    this.dateFrom = value;
    this.page = 0;
    void this.loadEntries();
  }

  setDateTo(value: string) {
    this.dateTo = value;
    this.page = 0;
    void this.loadEntries();
  }

  clearFilters() {
    this.serviceFilter = "all";
    this.actionFilter = "all";
    this.userFilter = "";
    this.recordFilter = "";
    this.clientFilter = "";
    this.dateFrom = "";
    this.dateTo = "";
    this.page = 0;
    void this.loadEntries();
  }

  setPage(page: number) {
    this.page = page;
    void this.loadEntries();
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 0;
    void this.loadEntries();
  }

  toggleExpanded(id: number) {
    const next = new Set(this.expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.expandedIds = next;
  }

  // ---- Loading ------------------------------------------------------------

  get query(): Record<string, unknown> {
    const query: Record<string, unknown> = {
      $limit: this.pageSize,
      $skip: this.page * this.pageSize,
      $sort: { createdAt: -1 },
    };
    if (this.serviceFilter !== "all") query.entityType = this.serviceFilter;
    if (this.actionFilter !== "all") query.action = this.actionFilter;
    if (this.userFilter) query.userId = Number(this.userFilter);
    if (this.recordFilter.trim()) query.entityId = this.recordFilter.trim();
    if (this.clientFilter.trim()) query.clientId = this.clientFilter.trim();

    const createdAt: Record<string, string> = {};
    // The query validator requires full date-time strings; expand the local
    // YYYY-MM-DD inputs to inclusive day bounds.
    if (this.dateFrom) createdAt.$gte = `${this.dateFrom}T00:00:00.000Z`;
    if (this.dateTo) createdAt.$lte = `${this.dateTo}T23:59:59.999Z`;
    if (Object.keys(createdAt).length > 0) query.createdAt = createdAt;

    return query;
  }

  async loadEntries() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      // Server-paged: read the current page + total off the find RESPONSE
      // rather than the entity cache (which would union prior pages).
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered as "No audit entries match the current filters."
      const result = await findOrThrow<FindResult<AuditLogEntry> | null>(
        this.auditLogService,
        { query: this.query }
      );
      // Signed out meanwhile: these are the previous person's audit rows.
      if (ended()) return;
      const rows = result?.data ?? [];
      runInAction(() => {
        this.entries = rows;
        this.total = typeof result?.total === "number" ? result.total : rows.length;
        this.expandedIds = new Set();
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.entries = [];
        this.total = 0;
        this.error = loadErrorMessage(err, "Failed to load audit log");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Best-effort user directory load so the grid shows names, not raw ids. */
  async loadUsers() {
    if (this.usersLoaded) return;
    this.usersLoaded = true;
    const ended = sessionMark(this.rootStore);
    try {
      const result = (await this.userService?.find({
        query: { $limit: 200 },
      })) as FindResult<UserSummary> | null;
      if (ended()) return;
      const users = result?.data ?? [];
      runInAction(() => {
        const next = new Map<number, string>();
        for (const user of users) {
          const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
          next.set(user.id, name || user.email || `user #${user.id}`);
        }
        this.userNames = next;
      });
    } catch {
      // Non-fatal: rows fall back to "user #<id>".
    }
  }

  async load() {
    await Promise.all([this.loadEntries(), this.loadUsers()]);
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.entries = [];
    this.total = 0;
    this.loading = false;
    this.error = null;
    this.serviceFilter = "all";
    this.actionFilter = "all";
    this.userFilter = "";
    this.recordFilter = "";
    this.clientFilter = "";
    this.dateFrom = "";
    this.dateTo = "";
    this.page = 0;
    this.pageSize = 50;
    this.expandedIds = new Set();
    this.userNames = new Map();
    this.usersLoaded = false;
  }
}

export default AuditLogStore;
